/**
 * Pool de elementos de mídia.
 *
 * Criar um `<video>` por clip esgota o limite de decodificadores do navegador numa
 * timeline densa. O pool reaproveita elementos por asset e mantém um teto explícito —
 * degradar de forma previsível é melhor que travar sem aviso.
 */
export interface MediaSource {
  assetId: string
  url: string
  hasVideo: boolean
  hasAudio: boolean
  /** Imagem parada (inclui SVG dos elementos da biblioteca). */
  isStill?: boolean
}

export interface PooledMedia {
  element: HTMLVideoElement | HTMLAudioElement | HTMLImageElement
  assetId: string
  ready: boolean
  lastUsed: number
}

const MAX_ELEMENTS = 12

export type MediaErrorHandler = (assetId: string, message: string) => void

export class MediaPool {
  private readonly pool = new Map<string, PooledMedia>()
  private readonly audioContext: AudioContext
  private readonly nodes = new Map<string, { source: MediaElementAudioSourceNode; gain: GainNode }>()
  private readonly destination: AudioNode
  /**
   * Container oculto no DOM.
   *
   * Um `<video>` desanexado carrega de forma inconsistente entre navegadores — a
   * prioridade de rede cai e alguns nem chegam a decodificar. Mantê-los no documento,
   * fora da tela, é o que todo editor sério faz.
   */
  private readonly container: HTMLDivElement
  private onError: MediaErrorHandler | null = null

  constructor(audioContext: AudioContext, destination: AudioNode) {
    this.audioContext = audioContext
    this.destination = destination
    this.container = document.createElement('div')
    this.container.setAttribute('aria-hidden', 'true')
    this.container.style.cssText =
      'position:fixed;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;pointer-events:none'
    document.body.appendChild(this.container)
  }

  get(source: MediaSource): PooledMedia {
    const existing = this.pool.get(source.assetId)
    if (existing) {
      existing.lastUsed = performance.now()
      return existing
    }

    this.evictIfNeeded()

    const element = source.isStill
      ? document.createElement('img')
      : source.hasVideo
        ? document.createElement('video')
        : document.createElement('audio')

    // A ORDEM importa: `crossOrigin` precisa estar definido ANTES de `src`. Definir
    // depois faz o navegador iniciar o download em modo no-cors, e o canvas fica
    // contaminado — `drawImage` passa, mas o frame nunca aparece.
    element.crossOrigin = 'anonymous'
    if (!(element instanceof HTMLImageElement)) element.preload = 'auto'
    if (element instanceof HTMLVideoElement) {
      element.playsInline = true
      element.muted = false
    }
    element.src = source.url
    this.container.appendChild(element)
    if (!(element instanceof HTMLImageElement)) element.load()

    const entry: PooledMedia = { element, assetId: source.assetId, ready: false, lastUsed: performance.now() }
    // Imagem avisa por `load`; vídeo e áudio por `loadeddata`.
    element.addEventListener(element instanceof HTMLImageElement ? 'load' : 'loadeddata', () => {
      entry.ready = true
    })
    element.addEventListener('error', () => {
      entry.ready = false
      // Um preview preto e silencioso é o pior resultado possível: o usuário não tem
      // como saber se o problema é o arquivo, a rede ou o navegador. Dizer é o mínimo.
      const code = playable(element)?.error?.code
      const detail =
        code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED
          ? 'este navegador não consegue decodificar o formato do arquivo'
          : code === MediaError.MEDIA_ERR_NETWORK
            ? 'falha de rede ao carregar a mídia'
            : code === MediaError.MEDIA_ERR_DECODE
              ? 'erro ao decodificar a mídia'
              : 'não foi possível carregar a mídia'
      this.onError?.(source.assetId, detail)
    })
    // O áudio passa pelo Web Audio para receber volume, fades e keyframes; sem isso,
    // só restaria o `volume` do elemento, que não tem curva nem automação.
    const media = playable(element)
    if (source.hasAudio && media) {
      try {
        const node = this.audioContext.createMediaElementSource(media)
        const gain = this.audioContext.createGain()
        node.connect(gain)
        gain.connect(this.destination)
        this.nodes.set(source.assetId, { source: node, gain })
      } catch {
        // Alguns navegadores recusam um segundo MediaElementSource no mesmo elemento;
        // nesse caso o áudio ainda toca, só sem automação.
      }
    }

    this.pool.set(source.assetId, entry)
    return entry
  }

  setErrorHandler(handler: MediaErrorHandler): void {
    this.onError = handler
  }

  gainFor(assetId: string): GainNode | null {
    return this.nodes.get(assetId)?.gain ?? null
  }

  has(assetId: string): boolean {
    return this.pool.has(assetId)
  }

  /** Pausa tudo que não está sendo usado agora — som fantasma é o bug mais irritante. */
  pauseAllExcept(activeAssetIds: Set<string>): void {
    for (const [assetId, entry] of this.pool) {
      const media = playable(entry.element)
      if (media && !activeAssetIds.has(assetId) && !media.paused) media.pause()
    }
  }

  pauseAll(): void {
    for (const entry of this.pool.values()) playable(entry.element)?.pause()
  }

  private evictIfNeeded(): void {
    if (this.pool.size < MAX_ELEMENTS) return
    let oldest: PooledMedia | null = null
    for (const entry of this.pool.values()) {
      if (!oldest || entry.lastUsed < oldest.lastUsed) oldest = entry
    }
    if (!oldest) return
    const media = playable(oldest.element)
    media?.pause()
    oldest.element.removeAttribute('src')
    media?.load()
    oldest.element.remove()
    this.nodes.delete(oldest.assetId)
    this.pool.delete(oldest.assetId)
  }

  dispose(): void {
    for (const entry of this.pool.values()) {
      playable(entry.element)?.pause()
      entry.element.removeAttribute('src')
      entry.element.remove()
    }
    this.pool.clear()
    this.nodes.clear()
    this.container.remove()
  }
}

/** Imagem não toca nem pausa; separar aqui evita um `instanceof` em cada chamada. */
function playable(
  element: HTMLVideoElement | HTMLAudioElement | HTMLImageElement,
): HTMLMediaElement | null {
  return element instanceof HTMLImageElement ? null : element
}
